import {showNotification, useTranslationContext} from 'platform/components';

import {isNotNil} from 'ramda';

import {downloadImageFile, Nullish} from 'shared/web';

import {platformLightboxQueryParams} from '../consts/platformLightboxQueryParams';
import {LightboxAction} from '../types/LightboxAction';

interface CreateActionsOptions<T extends Asset> {
  allowShare?: boolean;
  allowDownload?: boolean;
  customActions?: LightboxAction[] | ((asset: T) => LightboxAction[]);
}

/**
 * Base asset interface - requires only id and url
 */
interface Asset {
  id: string;
  url?: string | Nullish;
}

/**
 * Type that represents an asset with actions added by the factory
 */
type AssetWithActions<T extends Asset> = T & {
  actions: LightboxAction[];
};

/**
 * Creates a function that generates lightbox actions with translations pre-bound.
 * This factory function returns methods that work at the array level, automatically
 * computing activeIndex and eliminating redundant parameter passing.
 *
 * @returns An object with methods to add actions to asset arrays
 *
 * @example
 * const actionsFactory = useLightboxActionsFactory();
 *
 * // Add share-only actions to an array of assets
 * const assetsWithActions = actionsFactory.addShareOnlyActions(assets);
 *
 * @example
 * // Add full actions (share + download) to an array of assets
 * const assetsWithActions = actionsFactory.addFullActions(assets);
 *
 * @example
 * // Add custom actions configuration with static actions
 * const customActions = [{ id: 'custom', icon: 'action/edit', onClick: (assetId) => {} }];
 * const assetsWithActions = actionsFactory.addCustomActions(assets, {
 *   allowShare: true,
 *   allowDownload: false,
 *   customActions
 * });
 *
 * @example
 * // Add asset-dependent custom actions
 * const assetsWithActions = actionsFactory.addCustomActions(assets, {
 *   allowShare: true,
 *   customActions: (asset) => [
 *     {
 *       id: 'edit',
 *       icon: asset.isEditable ? 'action/edit' : 'action/view',
 *       onClick: () => asset.isEditable ? editAsset(asset.id) : viewAsset(asset.id)
 *     }
 *   ]
 * });
 *
 * @example
 * // With custom asset type
 * interface MyAsset {
 *   id: string;
 *   url: string;
 *   title: string;
 *   isEditable: boolean;
 * }
 * const myAssets: MyAsset[] = [...];
 * const assetsWithActions = actionsFactory.addFullActions(myAssets, (asset) => [
 *   { id: 'info', icon: 'info', onClick: () => showInfo(asset.title) }
 * ]);
 * // Result type: (MyAsset & { actions: LightboxAction[] })[]
 */
export function useLightboxActionsFactory() {
  const t = useTranslationContext();

  const createActionsForAsset = <T extends Asset>(
    asset: T,
    activeIndex: number,
    options?: CreateActionsOptions<T>
  ): LightboxAction[] => {
    const actions: LightboxAction[] = [];
    const isBase64Image = asset.url?.startsWith('data:');

    // Add custom actions (static or asset-dependent)
    if (options?.customActions) {
      const customActions =
        typeof options.customActions === 'function'
          ? options.customActions(asset)
          : options.customActions;

      actions.push(...customActions);
    }

    // Share action
    if (options?.allowShare && !isBase64Image) {
      actions.push({
        icon: 'social/share',
        onClick: async () => {
          const url =
            window.location.href +
            `&${platformLightboxQueryParams.LIGHTBOX_DETAIL_INDEX}=${activeIndex}`;
          if (navigator.share) {
            try {
              await navigator.share({
                title: document.title,
                url,
              });
            } catch (err: unknown) {
              if (!(err instanceof Error && err.toString().includes('AbortError'))) {
                showNotification.error(t('platform.lightbox.share-error'));
              }
            }
          } else {
            await navigator.clipboard.writeText(url);
            showNotification.info(t('platform.lightbox.copy-to-clipboard'));
          }
        },
        id: `shareBtn-${asset.id}`,
      });
    }

    // Download action
    if (options?.allowDownload) {
      actions.push({
        icon: 'content/save_alt',
        onClick: async () => {
          isNotNil(asset.url) &&
            (await downloadImageFile(asset.url).catch(() => {
              showNotification.error(t('platform.lightbox.downloadError'));
            }));
        },
        id: `downloadBtn-${asset.id}`,
      });
    }

    return actions;
  };

  const addActionsToAssets = <T extends Asset>(
    assets?: T[] | Nullish,
    options?: CreateActionsOptions<T>
  ): AssetWithActions<T>[] | Nullish =>
    assets?.map((asset, index) => ({
      ...asset,
      actions: createActionsForAsset(asset, index, options),
    }));

  return {
    /**
     * Adds share-only actions to an array of assets.
     * The activeIndex is automatically computed based on array position.
     *
     * @template T - The input asset type (must have id and url)
     * @param assets - Array of assets to add actions to
     * @param customActions - Optional custom actions (static array or function that receives the asset)
     * @returns Array of assets with actions added, preserving the original type
     */
    addShareOnlyActions: <T extends Asset>(
      assets?: T[] | Nullish,
      customActions?: LightboxAction[] | ((asset: T) => LightboxAction[])
    ): AssetWithActions<T>[] | Nullish =>
      addActionsToAssets(assets, {allowShare: true, allowDownload: false, customActions}),

    /**
     * Adds full set of default actions (share + download) to an array of assets.
     * The activeIndex is automatically computed based on array position.
     *
     * @template T - The input asset type (must have id and url)
     * @param assets - Array of assets to add actions to
     * @param customActions - Optional custom actions (static array or function that receives the asset)
     * @returns Array of assets with actions added, preserving the original type
     */
    addFullActions: <T extends Asset>(
      assets?: T[] | Nullish,
      customActions?: LightboxAction[] | ((asset: T) => LightboxAction[])
    ): AssetWithActions<T>[] | Nullish =>
      addActionsToAssets(assets, {allowShare: true, allowDownload: true, customActions}),

    /**
     * Adds custom actions configuration to an array of assets.
     * The activeIndex is automatically computed based on array position.
     *
     * @template T - The input asset type (must have id and url)
     * @param assets - Array of assets to add actions to
     * @param options - Configuration for which actions to include
     * @returns Array of assets with actions added, preserving the original type
     */
    addCustomActions: <T extends Asset>(
      assets?: T[] | Nullish,
      options?: CreateActionsOptions<T>
    ): AssetWithActions<T>[] | Nullish => addActionsToAssets(assets, options),
  };
}
